import { $getRoot } from "lexical";
import type { EditorState, SerializedEditorState } from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useRef, useState } from "react";

import LeafButton, { Variant } from "../../../../../common/leaf/LeafButton/LeafButton";
import { NotesToolBar } from "../NotesToolBar/NotesToolBar";
import { LIST_TRANSFORMERS, NOTE_NODES, notesOnError, notesTheme } from "../notesConfig";
import "./NotesEditor.scss";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/downlineProfile";

type Props = {
  initialContent: SerializedEditorState | null;
  onSave: (state: SerializedEditorState) => void;
  onCancel: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;
  allowEmptySave?: boolean;
  onContentChange?: () => void;
};

// The rich-text editor used to create or edit a note.
export const NotesEditor = ({
  initialContent,
  onSave,
  onCancel,
  isSaving = false,
  saveDisabled = false,
  allowEmptySave = false,
  onContentChange,
}: Props) => {
  const { t } = useTranslation(["downlineProfile"]);
  // Latest serialized editor state, kept ready to hand back on save.
  const editorStateRef = useRef<SerializedEditorState | null>(initialContent);
  const [isEmpty, setIsEmpty] = useState(initialContent === null);

  const editorConfig = {
    namespace: "NotesEditor",
    nodes: NOTE_NODES,
    theme: notesTheme,
    onError: notesOnError,
    editorState: initialContent ? JSON.stringify(initialContent) : undefined,
  };

  const onChange = (editorState: EditorState) => {
    editorStateRef.current = editorState.toJSON();
    onContentChange?.();
    editorState.read(() => {
      // Disable Save when the editor has no text characters.
      setIsEmpty($getRoot().getTextContent().trim().length === 0);
    });
  };

  const handleSave = () => {
    if ((isEmpty && !allowEmptySave) || saveDisabled || !editorStateRef.current) return;
    onSave(editorStateRef.current);
  };

  return (
    <div className="NotesEditor">
      <div className="NotesEditor__box">
        <LexicalComposer initialConfig={editorConfig}>
          <NotesToolBar />
          <OnChangePlugin onChange={onChange} ignoreSelectionChange ignoreHistoryMergeTagChange={false} />

          <div className="NotesEditor__body">
            <RichTextPlugin
              contentEditable={<ContentEditable className="NotesEditor__input" />}
              placeholder={
                <div className="NotesEditor__placeholder">
                  {t("notes.editor.placeholder", defaultContent.notes.editor.placeholder as string)}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={LIST_TRANSFORMERS} />
        </LexicalComposer>
      </div>

      <div className="NotesEditor__actions">
        <LeafButton
          clickAction={onCancel}
          id="notes-cancel"
          title={t("notes.editor.cancel", defaultContent.notes.editor.cancel as string)}
        >
          {t("notes.editor.cancel", defaultContent.notes.editor.cancel as string)}
        </LeafButton>
        <LeafButton
          clickAction={handleSave}
          id="notes-save"
          title={t("notes.editor.save", defaultContent.notes.editor.save as string)}
          variant={Variant.SOLID}
          isDisabled={(isEmpty && !allowEmptySave) || isSaving || saveDisabled}
        >
          {t("notes.editor.save", defaultContent.notes.editor.save as string)}
        </LeafButton>
      </div>
    </div>
  );
};
